import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { doGetClass2 } from '../../../store/class.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import ClassInfo2 from './Info2';
import ClassRegister2 from './Register2';
import { doGetClassStudents } from '../../../store/classStudent.slice';
import Loader from '../../Loader';
import ClassSchedule2 from './Schedule2';
import { doGetClassTimetables } from '../../../store/classTimetable.slice';
import QueryString from 'qs';
import TabItem from './TabItem';
import ClassAward from './Award';
import TestContainer from './TestContainer';
import ClassPoint from './Point';
import useNotification from '../../../hooks/useNotification';
import ClassScore2 from './Score2';
import ClassNotice2 from './Notice2';
import ClassArticlePane from './ClassArticlePane';
import ClassElearning from './ClassElearning';
import TableEmpty from '../../common/TableEmpty';
import CanbotReport from './CanbotReport';
import DailyMission from './DailyMission';

interface IProps {
  tab: string;
  setTab: (tab: string) => void;
  setIsExtended: (isExtended: boolean) => void;
  setRefreshTable: (refreshTable: boolean) => void;
}

const ClassTab: FC<IProps> = ({
  tab,
  setTab,
  setIsExtended,
  setRefreshTable,
}) => {
  const { t } = useTranslation();
  const { warningNotification, errorNotification } = useNotification();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  let { chosenClass, isLoading: isLoading1 } = useSelector(
    (state: RootState) => state.class,
  );
  // 불러둔 store의 내용을 하위 component에서 사용
  const { isLoading: isLoading2 } = useSelector(
    (state: RootState) => state.classStudent,
  );
  const { isLoading: isLoading3 } = useSelector(
    (state: RootState) => state.classTimetable,
  );
  const [classCode, setClassCode] = useState('');
  const [semesterCode, setSemesterCode] = useState('');
  const [prevTab, setPrevTab] = useState('');
  const [statuses, setStatuses] = useState<any>(['', '', '']);

  useEffect(()=> {
    if (!chosenClass) return;

    setSemesterCode(String(chosenClass.semesterCode));
  },[chosenClass])

  useEffect(() => {
    if (query.tab && query.tab === 'createClass') {
      setClassCode('');
    }
    if(location.search === ''){
      setClassCode('');
      setTab('info');
    }
    if (query.classCode) {
      const _classCode = String(query.classCode);
      const _semesterCode = String(query.semester);
      if (
        _classCode !== classCode ||
        (tab !== prevTab &&
          (prevTab === 'createClass' || prevTab === 'updateClass'))
      ) {
        doGetClassData(_classCode, Number(_semesterCode));
        setSemesterCode(_semesterCode);
        setPrevTab(tab);

        switch (tab) {
          case 'createClass':
          case 'updateClass':
            setTab('info');
            setPrevTab('info');
            break;
          case 'allocateStudent':
          case 'allocateResult':
          case 'attendance':
          case 'refund':
          case 'retire':
          case 'retireResult':
          case 'transfer':
          case 'transferCalculation':
            setTab('register');
            setPrevTab('register');
            break;
          case 'calendarDetail':
          case 'rollbook':
          case 'inputScore':
            setTab('schedule');
            setPrevTab('schedule');
            break;
          case 'testScore':
            setTab('test');
            setPrevTab('test');
            break;
        }
      }
    }
  }, [location]);

  useEffect(() => {
    if(classCode !== 'undefined') { 
      if (statuses.every((status: any) => status)) {
        sessionStorage.removeItem('statuses');

        if (statuses.includes('401')) {
          dispatch(doRemoveAuth());
        } else if (statuses.includes('403')) {
          warningNotification(t('common.message.1'));
          history.goBack();
        } else if (!statuses.every((status: any) => status === '200')) {
          errorNotification(t('common.message.error'));
        }
      }
    }
  }, [statuses]);

  const doGetClassData = (classCode: string, semesterCode?: number) => {
    getClass(classCode);
    getStudents(classCode, semesterCode);
    getTimetables(classCode);
    setClassCode(classCode);
  };

  const onSetStatuses = (index: number, status: number) => {
    let newStatuses: any = sessionStorage.getItem('statuses');

    if (newStatuses && newStatuses !== 'null') {
      newStatuses = JSON.parse(newStatuses);
    } else {
      newStatuses = [...statuses];
    }

    newStatuses[index] = String(status);
    setStatuses(newStatuses);
    sessionStorage.setItem('statuses', JSON.stringify(newStatuses));
  };

  const getClass = async (classCode: string) => {
    const res = await dispatch(doGetClass2(classCode));
    onSetStatuses(0, res?.status);
  };

  const getStudents = async (classCode: string, semesterCode?: number) => {
    const res = await dispatch(doGetClassStudents(classCode, semesterCode));
    onSetStatuses(1, res?.status);
  };

  const getTimetables = async (classCode: string) => {
    const res = await dispatch(doGetClassTimetables(classCode));
    onSetStatuses(2, res?.status);
  };

  const renderTabContent = () => {
    switch (tab) {
      case 'info':
      case 'createClass':
      case 'updateClass':
        return (
          <ClassInfo2
            tab={tab}
            setTab={onSetTab}
            doGetClassData={doGetClassData}
            setRefreshTable={setRefreshTable}
          />
        );
      case 'register':
      case 'allocateStudent':
      case 'allocateResult':
      case 'attendance':
      case 'refund':
      case 'retire':
      case 'retireResult':
      case 'transfer':
      case 'transferCalculation':
        return (
          <ClassRegister2
            tab={tab}
            setTab={onSetTab}
            getStudents={getStudents}
            semesterCode={Number(semesterCode)}
          />
        );
      case 'schedule':
      case 'calendarDetail':
      case 'rollbook':
      case 'inputScore':
        return (
          <ClassSchedule2
            tab={tab}
            setTab={onSetTab}
            setIsExtended={setIsExtended}
            getTimetables={getTimetables}
          />
        );
      case 'score':
        return <ClassScore2 />;
      case 'point':
        return <ClassPoint />;
      case 'notice':
        return (
          <ClassNotice2
            classCode={classCode}
          />
        );
      case 'test':
      case 'testScore':
        return (
          <TestContainer
            tab={tab}
            setTab={onSetTab}
          />
        );
      case 'article':
        return <ClassArticlePane />;
      case 'award':
        return <ClassAward />;
      case 'eLearning':
        return (
          <ClassElearning 
            classCode={classCode}
            semesterCode={semesterCode}
          />
        );
      case 'report':
        return (
          <CanbotReport 
            classCode={classCode}
          />
        );
      case 'daily':
        return (
          <DailyMission 
            classCode={classCode}
          />
        );
    }
  };

  const onSetTab = (
    tab: string,
    classCode: string = query.classCode ? String(query.classCode) : '',
    campusCode?: string,
    semesterCode?: string
    ) => {
      if(classCode) {
        //campusCode, semesterCode가 있는 경우에만 추가
        const param = QueryString.stringify({ 
          ...query
          , tab
          , classCode
          , ...(campusCode ? { campus: campusCode } : {}) 
          , ...(semesterCode ? { semester: semesterCode } : {}) 
        });
        history.push(`/class/class2?${param}`);
        return;
      }
      warningNotification(t('class.class.8'));
  };


  return (
    <div>
      {(isLoading1 || isLoading2 || isLoading3) && <Loader />}

      <div className="tab-content" id="custom-tabs-tabContent">
        {(classCode && chosenClass) || tab === 'createClass' ? (
          renderTabContent()
        ) : (
          <TableEmpty text={t('class.class.text33')} />
        )}
      </div>
    </div>
  );
};

export default ClassTab;